// src/components/announcements/PublicAnnouncementBanner.jsx
//
// Public runtime announcement banner
// ==================================
//
// PURPOSE
// -------
// Renders active public announcements in PublicLayout without coupling the
// feature to the voucher shop implementation.
//
// The first real target is /shop, but this component is intentionally generic:
// - PublicLayout passes the current React Router path,
// - backend decides which announcements match that path,
// - ShopPage remains focused on voucher-only checkout logic.
//
// IMPORTANT CROSS-DEPENDENCY RULE
// -------------------------------
// This component must not import shopService and must not know anything about
// vouchers/packages/orders/P24. It is a shell-level communication banner.
//
// LAYOUT CONTRACT
// ---------------
// ShopPage has its own carefully tuned 100dvh desktop viewport and nested
// scroll behaviour. If a normal banner is simply inserted above ShopPage, that
// extra height can make the bottom of the shop form difficult to reach.
//
// Therefore this component reports its rendered height to PublicLayout via
// onLayoutChange(). PublicLayout can expose this height as a CSS variable for
// diagnostics/future route-specific layout rules. The current CSS primarily
// relies on a flex-based PublicLayout: the banner takes its natural height and
// `.public-layout__content` receives the remaining viewport height. ShopPage
// then keeps scrolling inside its own `.shop-page-viewport`, which is essential
// because body/#root scrolling is globally disabled in this application.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchPublicActiveAnnouncements } from '../../services/announcementService';
import { getAnnouncementTickerDuration } from '../../utils/announcementTicker';
import './PublicAnnouncementBanner.css';

const REFRESH_MS = 3 * 60 * 1000;
const DEFAULT_SURFACE = 'public_shell_banner';

function getDismissStorageKey(announcement) {
  const version = announcement.updatedAt || announcement.createdAt || 'v1';
  return `hb_public_announcement_dismissed:${announcement.id}:${version}`;
}

function isDismissed(announcement) {
  if (!announcement?.is_dismissible) return false;

  try {
    return window.sessionStorage.getItem(getDismissStorageKey(announcement)) === '1';
  } catch {
    return false;
  }
}

function markDismissed(announcement) {
  try {
    window.sessionStorage.setItem(getDismissStorageKey(announcement), '1');
  } catch {
    // Ignore storage errors. Dismissal is a convenience only.
  }
}

function getSeverityClass(severity) {
  const normalized = String(severity || 'info').toLowerCase();
  if (['info', 'warning', 'error', 'success'].includes(normalized)) {
    return `public-announcement public-announcement--${normalized}`;
  }
  return 'public-announcement public-announcement--info';
}



const PublicAnnouncementBanner = ({
  currentPath,
  surface = DEFAULT_SURFACE,
  limit = 3,
  onLayoutChange,
}) => {
  const { i18n } = useTranslation();
  const containerRef = useRef(null);
  const [announcements, setAnnouncements] = useState([]);

  const language = i18n.resolvedLanguage || i18n.language || 'pl';
  const path = currentPath || '/';

  useEffect(() => {
    let cancelled = false;

    // Avoid briefly showing announcements from a previously visited public
    // route while the new route-specific query is still loading.
    setAnnouncements([]);

    const load = async () => {
      const data = await fetchPublicActiveAnnouncements({
        surface,
        path,
        lang: language,
        limit,
      });

      if (cancelled) return;

      setAnnouncements(Array.isArray(data) ? data : []);
    };

    load();

    const intervalId = window.setInterval(load, REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [language, limit, path, surface]);

  const visibleAnnouncements = useMemo(() => {
    return announcements.filter((announcement) => {
      const message = String(announcement?.message || '').trim();
      return message && !isDismissed(announcement);
    });
  }, [announcements]);

  useEffect(() => {
    if (typeof onLayoutChange !== 'function') return undefined;

    if (!visibleAnnouncements.length) {
      onLayoutChange({ visible: false, height: 0 });
      return undefined;
    }

    const updateLayout = () => {
      const element = containerRef.current;
      const height = element ? Math.ceil(element.getBoundingClientRect().height) : 0;
      onLayoutChange({ visible: height > 0, height });
    };

    updateLayout();

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      resizeObserver = new ResizeObserver(updateLayout);
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateLayout);

    return () => {
      window.removeEventListener('resize', updateLayout);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [onLayoutChange, visibleAnnouncements.length]);

  if (!visibleAnnouncements.length) {
    return null;
  }

  return (
    <section ref={containerRef} className="public-announcements" aria-label="Public announcements">
      {visibleAnnouncements.map((announcement) => (
        <article
          key={announcement.id}
          className={getSeverityClass(announcement.severity)}
          style={{
            "--public-announcement-duration": getAnnouncementTickerDuration(announcement),
          }}
        >
          <div className="public-announcement__viewport">
            <div className="public-announcement__track">
              <span className="public-announcement__message">{announcement.message}</span>

              {announcement.link_url ? (
                <a
                  className="public-announcement__link"
                  href={announcement.link_url}
                  target={announcement.link_url.startsWith('http') ? '_blank' : undefined}
                  rel={announcement.link_url.startsWith('http') ? 'noreferrer' : undefined}
                >
                  {announcement.link_label || 'More information'}
                </a>
              ) : null}
            </div>
          </div>

          {announcement.is_dismissible ? (
            <button
              type="button"
              className="public-announcement__dismiss"
              aria-label="Dismiss announcement"
              onClick={() => {
                markDismissed(announcement);
                setAnnouncements((prev) => prev.filter((item) => item.id !== announcement.id));
              }}
            >
              ×
            </button>
          ) : null}
        </article>
      ))}
    </section>
  );
};

export default PublicAnnouncementBanner;
